import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { cn } from "@/lib/utils";
import TakehomePayChart from '../../charts/TakehomePayChart';
import { IncomeTaxCalculatorAPIResponse } from '../../Types/Autogenerated/IncomeTaxCalculatorApiResponse';
import { IncomeTaxCalculatorRequestPayload } from '../../Types/Autogenerated/IncomeTaxCalculatorRequestPayload';
import { IncomesAdjustmentsAndDeductions } from '../../Types/Autogenerated/IncomesAdjustmentsAndDeductions';
import { Convert as FilingStatusConvert, FilingStatus } from '../../Types/Autogenerated/FilingStatus';
import { Convert as StateConvert } from '../../Types/Autogenerated/State';
import { FormData } from '../../App';
import { CurrencyInputOnChangeValues } from 'react-currency-input-field';
import { taxYearItems, stateItems, filingStatuses } from '../../Utils/Constants';
import TermsDescriptions from '../../components/TermsDescriptions';
import { takehomePayTerms } from '../../Utils/TermDefinitions';
import FloatingChartButton from '../../components/FloatingChartButton';
import ChartSummary from '../../components/ChartSummary';
import { generateTakehomePaySummary } from '../../Utils/ChartSummaryGenerators';
import { formatCurrency } from '../../Utils/chartUtils';
import AdditionalDeductions from '../../components/AdditionalDeductions';
import { AdditionalDeduction } from '../../types/AdditionalDeductions';


interface TakehomePayCalculatorPageProps {
  formData: FormData;
  selectedPage: string;
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onValueChange: (value: string | undefined, name?: string, values?: CurrencyInputOnChangeValues) => void;
  handleToggleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setSnackbarMessage: (value: React.SetStateAction<string>) => void;
  showSnackbar: boolean;
  snackbarMessage: string;
  setChartData: (value: React.SetStateAction<number[][] | null>) => void;
  setShowSnackbar: (value: React.SetStateAction<boolean>) => void;
  chartData: number[][] | null;
  personsToggle: boolean;
  // Hanlder for when a select is changed
  handleSelectChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}

const TakehomePayCalculatorPage: React.FC<TakehomePayCalculatorPageProps> = ({
  formData,
  selectedPage,
  handleInputChange,
  onValueChange,
  handleToggleChange,
  setSnackbarMessage,
  showSnackbar,
  snackbarMessage,
  setChartData,
  setShowSnackbar,
  chartData,
  personsToggle,
  handleSelectChange,
  // ... other props
}) => {
  // Helper functions for additional deductions
  const parseAdditionalDeductions = useCallback((jsonString: string): AdditionalDeduction[] => {
    try {
      const parsed = JSON.parse(jsonString) || [];
      // Ensure backward compatibility by adding reducesTaxableIncome field if missing
      return parsed.map((deduction: any) => ({
        ...deduction,
        reducesTaxableIncome: deduction.reducesTaxableIncome ?? false
      }));
    } catch {
      return [];
    }
  }, []);

  const handleAdditionalDeductionsEnabledChange = (enabled: boolean) => {
    handleInputChange({
      target: {
        name: 'additionalDeductionsEnabled',
        checked: enabled,
        type: 'checkbox'
      }
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleAdditionalDeductionsChange = (deductions: AdditionalDeduction[]) => {
    handleInputChange({
      target: {
        name: 'additionalDeductions',
        value: JSON.stringify(deductions),
        type: 'text'
      }
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleAdditionalDeductionsPerson1Change = (deductions: AdditionalDeduction[]) => {
    handleInputChange({
      target: {
        name: 'additionalDeductionsPerson1',
        value: JSON.stringify(deductions),
        type: 'text'
      }
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleAdditionalDeductionsPerson2Change = (deductions: AdditionalDeduction[]) => {
    handleInputChange({
      target: {
        name: 'additionalDeductionsPerson2',
        value: JSON.stringify(deductions),
        type: 'text'
      }
    } as React.ChangeEvent<HTMLInputElement>);
  };

  // Helper method to compute deduction totals
  const compute_total = useCallback((deductions: AdditionalDeduction[], income: number, only_tax_deductible: boolean = false): number => {
    return deductions.reduce((total, deduction) => {
      if (!deduction.amount || deduction.amount === '') return total;
      if (only_tax_deductible && !deduction.reducesTaxableIncome) return total;
      
      const amount = Number(deduction.amount);
      return total + (deduction.type === 'percentage' ? (amount * income) / 100 : amount);
    }, 0);
  }, []);
  const [previousFilingStatus, setPreviousFilingStatus] = useState<FilingStatus>(FilingStatus.Single);
  const [isLoading, setIsLoading] = useState(false);
  const [grossIncome, setGrossIncome] = useState<number>(0);
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Initialize previousFilingStatus when the component mounts
    setPreviousFilingStatus(formData.filingStatus as FilingStatus);
  }, [formData.filingStatus]);

  const handleTwoIncomesToggle = (checked: boolean) => {
    // Call the handleToggleChange function with a synthesized event
    handleToggleChange({
      target: {
        name: 'personsToggle',
        checked,
        id: 'persons',  // For legacy reasons "persons" is the id for the toggle switch that controls 1 or 2 incomes
        type: 'checkbox'
      }
    } as React.ChangeEvent<HTMLInputElement>);

    if (checked) {
      setPreviousFilingStatus(formData.filingStatus as FilingStatus);
      handleInputChange({ 
        target: { 
          name: 'filingStatus', 
          value: FilingStatus.MarriedFilingJointly.toString(),
          type: 'text'
        } 
      } as React.ChangeEvent<HTMLInputElement>);
    } else {
      handleInputChange({ 
        target: { 
          name: 'filingStatus', 
          value: previousFilingStatus.toString(), 
          type: 'text'
        } 
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const handleFilingStatusChange = (newStatus: string) => {
    handleInputChange({ 
      target: { 
        name: 'filingStatus', 
        value: newStatus 
      } 
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleStateChange = (newStatus: string) => {
    handleInputChange({ 
      target: { 
        name: 'state', 
        value: newStatus 
      } 
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleTaxYearChange = (newStatus: string) => {
    handleInputChange({ 
      target: { 
        name: 'taxYear', 
        value: newStatus 
      } 
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const formatNumber = (num: number | string) => {
    if (typeof num === 'string') {
      // Remove commas before parsing
      num = parseFloat(num.replace(/,/g, ''));
    }
    return isNaN(num) ? '' : num.toLocaleString('en-US');
  };

  const handleIncomeChange = (value: string, name: string) => {
    // Remove commas and convert to number
    const numericValue = value ? parseFloat(value.replace(/,/g, '')) : 0;
    onValueChange(numericValue.toString(), name);
  };

  const calculateTotalCharitableGiving = useCallback((): number[] => {
    if (!personsToggle) {
      return [(Number(formData.charitableGivingRate) * Number(formData.income)) / 100];
    } else {
      return [
        (Number(formData.charitableGivingRatePerson1) * Number(formData.incomePerson1)) / 100,
        (Number(formData.charitableGivingRatePerson2) * Number(formData.incomePerson2)) / 100
      ];
    }
  }, [personsToggle, formData]);

  const calculateAdditionalDeductions = useCallback((): number[] => {
    if (!formData.additionalDeductionsEnabled) {
      return personsToggle ? [0, 0] : [0];
    }

    if (!personsToggle) {
      const deductions = parseAdditionalDeductions(formData.additionalDeductions);
      const income = Number(formData.income);
      return [compute_total(deductions, income)];
    } else {
      const deductionsPerson1 = parseAdditionalDeductions(formData.additionalDeductionsPerson1);
      const deductionsPerson2 = parseAdditionalDeductions(formData.additionalDeductionsPerson2);
      const incomePerson1 = Number(formData.incomePerson1);
      const incomePerson2 = Number(formData.incomePerson2);

      return [
        compute_total(deductionsPerson1, incomePerson1),
        compute_total(deductionsPerson2, incomePerson2)
      ];
    }
  }, [personsToggle, formData, parseAdditionalDeductions, compute_total]);

  // New function to get detailed breakdown of additional deductions for chart tooltips
  const getAdditionalDeductionsBreakdown = useCallback((): { name: string; value: number }[] => {
    if (!formData.additionalDeductionsEnabled) {
      return [];
    }

    const breakdown: { name: string; value: number }[] = [];
    
    if (!personsToggle) {
      const deductions = parseAdditionalDeductions(formData.additionalDeductions);
      const income = Number(formData.income);
      
      deductions.forEach((deduction) => {
        if (deduction.amount && deduction.amount !== '' && deduction.title.trim()) {
          // Use helper for individual deduction calculation
          const calculatedValue = compute_total([deduction], income);
          if (calculatedValue > 0) {
            breakdown.push({
              name: deduction.title,
              value: calculatedValue
            });
          }
        }
      });
    } else {
      const deductionsPerson1 = parseAdditionalDeductions(formData.additionalDeductionsPerson1);
      const deductionsPerson2 = parseAdditionalDeductions(formData.additionalDeductionsPerson2);
      const incomePerson1 = Number(formData.incomePerson1);
      const incomePerson2 = Number(formData.incomePerson2);

      // Person 1 deductions
      deductionsPerson1.forEach((deduction) => {
        if (deduction.amount && deduction.amount !== '' && deduction.title.trim()) {
          // Use helper for individual deduction calculation
          const calculatedValue = compute_total([deduction], incomePerson1);
          if (calculatedValue > 0) {
            breakdown.push({
              name: `${deduction.title} (Person 1)`,
              value: calculatedValue
            });
          }
        }
      });

      // Person 2 deductions
      deductionsPerson2.forEach((deduction) => {
        if (deduction.amount && deduction.amount !== '' && deduction.title.trim()) {
          // Use helper for individual deduction calculation
          const calculatedValue = compute_total([deduction], incomePerson2);
          if (calculatedValue > 0) {
            breakdown.push({
              name: `${deduction.title} (Person 2)`,
              value: calculatedValue
            });
          }
        }
      });
    }

    return breakdown;
  }, [personsToggle, formData, parseAdditionalDeductions, compute_total]);

  // Function to calculate only tax-reducing additional deductions for API
  const calculateTaxReducingDeductions = useCallback((): number[] => {
    if (!formData.additionalDeductionsEnabled) {
      return personsToggle ? [0, 0] : [0];
    }

    if (!personsToggle) {
      const deductions = parseAdditionalDeductions(formData.additionalDeductions);
      const income = Number(formData.income);
      return [compute_total(deductions, income, true)];
    } else {
      const deductionsPerson1 = parseAdditionalDeductions(formData.additionalDeductionsPerson1);
      const deductionsPerson2 = parseAdditionalDeductions(formData.additionalDeductionsPerson2);
      const incomePerson1 = Number(formData.incomePerson1);
      const incomePerson2 = Number(formData.incomePerson2);

      return [
        compute_total(deductionsPerson1, incomePerson1, true),
        compute_total(deductionsPerson2, incomePerson2, true)
      ];
    }
  }, [personsToggle, formData, parseAdditionalDeductions, compute_total]);

  const calculateTotalIncome = useCallback((): number => {
    return personsToggle 
      ? Number(formData.incomePerson1) + Number(formData.incomePerson2) 
      : Number(formData.income);
  }, [personsToggle, formData]);

  const processChartData = useCallback((data: IncomeTaxCalculatorAPIResponse): number[][] => {
    const charitableGivings = calculateTotalCharitableGiving();
    const totalCharitableGiving = charitableGivings.reduce((sum, giving) => sum + giving, 0);
    const additionalDeductions = calculateAdditionalDeductions();
    const totalAdditionalDeductions = additionalDeductions.reduce((sum, deduction) => sum + deduction, 0);
    const totalIncome = calculateTotalIncome();

    return [[
      totalIncome - (totalCharitableGiving + totalAdditionalDeductions + data.total_tax_owed),
      totalCharitableGiving,
      totalAdditionalDeductions,
      data.federal.income_tax_owed.reduce((a, b) => a + b, 0),
      data.social_security.income_tax_owed.reduce((a, b) => a + b, 0),
      data.state.income_tax_owed.reduce((a, b) => a + b, 0),
      data.medicare.income_tax_owed.reduce((a, b) => a + b, 0),
    ]];
  }, [calculateTotalCharitableGiving, calculateAdditionalDeductions, calculateTotalIncome]);

  const makeIncomeTaxCalculatorUrl = () => {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://127.0.0.1:5000/TakehomePayCalculator';
    }
    
    // Use predictable CloudFront URL
    const domain = window.location.hostname === 'moneymath.fyi' 
      ? 'moneymath.fyi' 
      : window.location.hostname;
    
    return `https://${domain}/api/takehome-pay`;
  };

  // Memoize input data to prevent unnecessary re-renders
  const inputData = useMemo(() => ({
    income: personsToggle ? [formData.incomePerson1, formData.incomePerson2] : [formData.income],
    charitableGiving: personsToggle 
      ? [formData.charitableGivingRatePerson1, formData.charitableGivingRatePerson2]
      : [formData.charitableGivingRate],
    taxYear: formData.taxYear,
    state: formData.state,
    filingStatus: formData.filingStatus,
    personsToggle,
    additionalDeductionsEnabled: formData.additionalDeductionsEnabled,
    additionalDeductions: formData.additionalDeductions,
    additionalDeductionsPerson1: formData.additionalDeductionsPerson1,
    additionalDeductionsPerson2: formData.additionalDeductionsPerson2
  }), [
    formData.income,
    formData.incomePerson1,
    formData.incomePerson2,
    formData.charitableGivingRate,
    formData.charitableGivingRatePerson1,
    formData.charitableGivingRatePerson2,
    formData.taxYear,
    formData.state,
    formData.filingStatus,
    personsToggle,
    formData.additionalDeductionsEnabled,
    formData.additionalDeductions,
    formData.additionalDeductionsPerson1,
    formData.additionalDeductionsPerson2
  ]);

  const makeAPIRequest = useCallback(async () => {
    if (isLoading) return; // Prevent multiple simultaneous requests
    
    setIsLoading(true);
    try {
      const url = makeIncomeTaxCalculatorUrl();
      const charitableGivings = !inputData.personsToggle
        ? [(Number(inputData.charitableGiving[0]) * Number(inputData.income[0])) / 100]
        : [
            (Number(inputData.charitableGiving[0]) * Number(inputData.income[0])) / 100,
            (Number(inputData.charitableGiving[1]) * Number(inputData.income[1])) / 100
          ];

      // Calculate additional deductions for API (only tax-reducing ones)
      const additionalDeductionsForAPI = calculateTaxReducingDeductions();

      const incomes_adjustments_and_deductions: IncomesAdjustmentsAndDeductions[] = !inputData.personsToggle
        ? [{
            salaries_and_wages: Number(inputData.income[0]),
            charitable_contributions: charitableGivings[0],
            other_adjustments: additionalDeductionsForAPI[0],
          }]
        : [{
            salaries_and_wages: Number(inputData.income[0]),
            charitable_contributions: charitableGivings[0],
            other_adjustments: additionalDeductionsForAPI[0],
          }, {
            salaries_and_wages: Number(inputData.income[1]),
            charitable_contributions: charitableGivings[1],
            other_adjustments: additionalDeductionsForAPI[1],
          }];

      const payload: IncomeTaxCalculatorRequestPayload = {
        tax_year: parseInt(inputData.taxYear, 10),
        state: StateConvert.toState(JSON.stringify(inputData.state)),
        filing_status: FilingStatusConvert.toFilingStatus(JSON.stringify(inputData.filingStatus)),
        incomes_adjustments_and_deductions: incomes_adjustments_and_deductions,
        state_data: { exemptions: 0 }
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Unknown error occurred');
      }

      const data: IncomeTaxCalculatorAPIResponse = await response.json();
      
      // Calculate and store gross income for summary
      const totalGrossIncome = personsToggle 
        ? Number(inputData.income[0]) + Number(inputData.income[1])
        : Number(inputData.income[0]);
      setGrossIncome(totalGrossIncome);
      
      setChartData(processChartData(data));
    } catch (error) {
      console.error('Error:', error);
      setSnackbarMessage(error instanceof Error ? error.message : 'An unknown error occurred');
      setShowSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  }, [inputData, processChartData, setChartData, setSnackbarMessage, setShowSnackbar, isLoading, personsToggle, calculateTaxReducingDeductions]);

  // Track previous input data for change detection
  const previousInputDataRef = useRef<string>('');

  // Auto-calculate with debouncing for API calls - only when inputs actually change
  useEffect(() => {
    const currentInputDataString = JSON.stringify(inputData);
    
    // Only make API request if input data has actually changed
    if (currentInputDataString !== previousInputDataRef.current) {
      const timeoutId = setTimeout(() => {
        makeAPIRequest();
        previousInputDataRef.current = currentInputDataString;
      }, 500); // 500ms debounce for quick API calls

      return () => clearTimeout(timeoutId);
    }
  }, [inputData, makeAPIRequest]);

  const labels: string[] = [
    "Take-home Pay",
    "Charity",
    "Additional Deductions",
    "Federal",
    "Social Security",
    "State",
    "Medicare",
  ];

  // Assuming taxYearItems is an object with year labels as keys and year values as values
  const sortedTaxYears = Object.entries(taxYearItems)
    .sort((a, b) => parseInt(b[1]) - parseInt(a[1])); // Sort in descending order

  return (
    <div className="takehome-pay-calculator w-full px-2 sm:px-4 lg:px-6 financial-app">
      {/* Professional Header */}
      <div className="hero-section mb-6 -mx-2 sm:-mx-4 lg:-mx-6 px-2 sm:px-4 lg:px-6">
        <div className="professional-layout">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-2 tracking-tight">
            {selectedPage}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Calculate your exact take-home pay after federal and state taxes
          </p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 mb-6">
        {/* Form Section */}
        <div className="lg:w-2/5 min-w-0">
          <Card className="w-full shadow-lg hover:shadow-xl transition-all duration-300">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="w-2 h-8 bg-gradient-to-b from-primary to-accent rounded-full"></div>
                <h2 className={cn("text-xl font-semibold text-card-foreground")}>Tax Calculator</h2>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Enter your income details to see your detailed tax breakdown
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Income Configuration */}
              <div className="p-4 rounded-lg bg-muted/30 border border-border">
                <div className={cn("flex items-center justify-between")}>
                  <div>
                    <Label htmlFor="personsToggle" className="text-sm font-medium text-foreground">
                      Multiple Income Sources
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Calculate for dual income households
                    </p>
                  </div>
                  <Switch
                    id="personsToggle"
                    checked={personsToggle}
                    onCheckedChange={handleTwoIncomesToggle}
                  />
                </div>
              </div>
              
              {/* Income Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-semibold text-foreground">Income Details</h3>
                  <div className="h-px bg-border flex-1"></div>
                </div>
                
                <div className={cn("grid gap-6", personsToggle ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1")}>
                  <div className={cn("space-y-4")}>
                    <div className="space-y-2">
                      <Label htmlFor={personsToggle ? "incomePerson1" : "income"} className="text-sm font-medium text-foreground flex items-center gap-2">
                        {personsToggle ? "Primary Income" : "Annual Gross Income"}
                        <span className="text-xs text-muted-foreground">(before taxes)</span>
                      </Label>
                      <div className={cn("relative group")}>
                        <span className={cn("absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground text-sm group-focus-within:text-primary transition-colors")}>$</span>
                        <Input
                          id={personsToggle ? "incomePerson1" : "income"}
                          name={personsToggle ? "incomePerson1" : "income"}
                          type="text"
                          placeholder="150,000"
                          value={formatNumber(personsToggle ? formData.incomePerson1 : formData.income)}
                          onChange={(e) => handleIncomeChange(e.target.value, personsToggle ? 'incomePerson1' : 'income')}
                          className={cn("pl-8 text-lg font-medium")}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor={personsToggle ? "charitableGivingRatePerson1" : "charitableGivingRate"} className="text-sm font-medium text-foreground">
                        {personsToggle ? "Charitable Giving %" : "Charitable Giving"}
                      </Label>
                      <div className={cn("relative group")}>
                        <Input
                          id={personsToggle ? "charitableGivingRatePerson1" : "charitableGivingRate"}
                          name={personsToggle ? "charitableGivingRatePerson1" : "charitableGivingRate"}
                          type="number"
                          placeholder="10"
                          value={personsToggle ? formData.charitableGivingRatePerson1 : formData.charitableGivingRate}
                          onChange={handleInputChange}
                          className={cn("pr-8")}
                        />
                        <span className={cn("absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground text-sm group-focus-within:text-primary transition-colors")}>%</span>
                      </div>
                    </div>
                  </div>

                  {personsToggle && (
                    <div className={cn("space-y-4 border-l-2 border-border pl-4")}>
                      <div className="space-y-2">
                        <Label htmlFor="incomePerson2" className="text-sm font-medium text-foreground flex items-center gap-2">
                          Secondary Income
                          <span className="text-xs text-muted-foreground">(before taxes)</span>
                        </Label>
                        <div className={cn("relative group")}>
                          <span className={cn("absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground text-sm group-focus-within:text-primary transition-colors")}>$</span>
                          <Input
                            id="incomePerson2"
                            name="incomePerson2"
                            type="text"
                            placeholder="80,000"
                            value={formatNumber(formData.incomePerson2)}
                            onChange={(e) => handleIncomeChange(e.target.value, 'incomePerson2')}
                            className={cn("pl-8 text-lg font-medium")}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="charitableGivingRatePerson2" className="text-sm font-medium text-foreground">
                          Charitable Giving %
                        </Label>
                        <div className={cn("relative group")}>
                          <Input
                            id="charitableGivingRatePerson2"
                            name="charitableGivingRatePerson2"
                            type="number"
                            placeholder="8"
                            value={formData.charitableGivingRatePerson2}
                            onChange={handleInputChange}
                            className={cn("pr-8")}
                          />
                          <span className={cn("absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground text-sm group-focus-within:text-primary transition-colors")}>%</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Tax Configuration Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-semibold text-foreground">Tax Configuration</h3>
                  <div className="h-px bg-border flex-1"></div>
                </div>
                
                <div className={cn("grid grid-cols-1 sm:grid-cols-2 gap-4")}>
                  <div className="space-y-2">
                    <Label htmlFor="taxYear" className="text-sm font-medium text-foreground">Tax Year</Label>
                    <Select value={formData.taxYear} onValueChange={handleTaxYearChange}>
                      <SelectTrigger id="taxYear" className="h-11">
                        <SelectValue placeholder="Select year" />
                      </SelectTrigger>
                      <SelectContent>
                        {sortedTaxYears.map(([key, value]) => (
                          <SelectItem key={value} value={value}>{key}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="state" className="text-sm font-medium text-foreground">State of Residence</Label>
                    <Select value={formData.state} onValueChange={handleStateChange}>
                      <SelectTrigger id="state" className="h-11">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(stateItems).map(([key, value]) => (
                          <SelectItem key={value} value={value}>{key}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="filingStatus" className="text-sm font-medium text-foreground flex items-center gap-2">
                    Filing Status
                    {personsToggle && (
                      <span className="text-xs text-muted-foreground">(auto-set for dual income)</span>
                    )}
                  </Label>
                  <Select value={formData.filingStatus} onValueChange={handleFilingStatusChange} disabled={personsToggle}>
                    <SelectTrigger id="filingStatus" className="h-11">
                      <SelectValue placeholder="Select filing status" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(filingStatuses).map(([key, value]) => (
                        <SelectItem key={key} value={key}>{value}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
              </div>
            </CardContent>
          </Card>

          {/* Additional Deductions Section */}
          <div className="mt-6">
            <AdditionalDeductions
              enabled={formData.additionalDeductionsEnabled}
              deductions={parseAdditionalDeductions(formData.additionalDeductions)}
              personsToggle={personsToggle}
              deductionsPerson1={parseAdditionalDeductions(formData.additionalDeductionsPerson1)}
              deductionsPerson2={parseAdditionalDeductions(formData.additionalDeductionsPerson2)}
              onEnabledChange={handleAdditionalDeductionsEnabledChange}
              onDeductionsChange={handleAdditionalDeductionsChange}
              onDeductionsPerson1Change={handleAdditionalDeductionsPerson1Change}
              onDeductionsPerson2Change={handleAdditionalDeductionsPerson2Change}
            />
          </div>
        </div>

        {/* Enhanced Chart Section */}
        <div className="lg:w-3/5 min-w-0" ref={chartRef}>
          <Card className="w-full h-full shadow-lg hover:shadow-xl transition-all duration-300">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-8 bg-gradient-to-b from-accent to-primary rounded-full"></div>
                  <div>
                    <h2 className="text-xl font-semibold text-card-foreground">Tax Breakdown</h2>
                    <p className="text-sm text-muted-foreground">
                      Visual breakdown of your income after taxes
                    </p>
                  </div>
                </div>
                {chartData && !isLoading && (
                  <div className="text-right">
                    <div className="text-sm text-muted-foreground">Take-home</div>
                    <div className="text-2xl font-bold text-primary">
                      {formatCurrency(chartData[0][0] || 0)}
                    </div>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="h-full">
              {chartData && !isLoading ? (
                <div className="h-full flex flex-col">
                  <div className="flex-grow">
                    <TakehomePayChart 
                      labels={labels} 
                      chartData={chartData} 
                      additionalDeductionsBreakdown={getAdditionalDeductionsBreakdown()}
                    />
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center min-h-[400px]">
                  <div className="flex flex-col items-center gap-4">
                    {isLoading && (
                      <div className="relative">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary/20 border-t-primary"></div>
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary/10 to-accent/10"></div>
                      </div>
                    )}
                    <div className="text-center">
                      <div className="text-lg font-medium text-foreground mb-1">
                        {isLoading ? 'Calculating your take-home pay...' : 'Ready to calculate'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {isLoading ? 'Processing tax calculations' : 'Enter your income details to see results'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Enhanced Chart Summary Section */}
      {chartData && !isLoading && (
        <div className="mt-6">
          <Card className="shadow-lg">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-2 h-8 bg-gradient-to-b from-success to-primary rounded-full"></div>
                <div>
                  <h2 className="text-xl font-semibold text-card-foreground">Detailed Breakdown</h2>
                  <p className="text-sm text-muted-foreground">
                    Complete analysis of your take-home pay calculation
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ChartSummary 
                summaryItems={generateTakehomePaySummary(chartData, grossIncome)}
                title=""
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Professional Terms Section */}
      <div className="mt-6">
        <Card className="shadow-lg">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-2 h-8 bg-gradient-to-b from-muted-foreground to-secondary rounded-full"></div>
              <div>
                <h2 className="text-xl font-semibold text-card-foreground">Understanding Your Taxes</h2>
                <p className="text-sm text-muted-foreground">
                  Learn about the different components of your tax calculation
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <TermsDescriptions 
              terms={takehomePayTerms} 
              title=""
            />
          </CardContent>
        </Card>
      </div>
      
      {/* Enhanced Error Display */}
      {showSnackbar && (
        <div className="fixed bottom-4 left-4 right-4 z-50 max-w-md mx-auto">
          <div className="bg-destructive text-destructive-foreground p-4 rounded-lg shadow-lg border border-destructive/20 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-destructive-foreground rounded-full"></div>
              <div className="font-medium">Calculation Error</div>
            </div>
            <div className="mt-1 text-sm opacity-90">
              {snackbarMessage}
            </div>
          </div>
        </div>
      )}
      
      <FloatingChartButton
        chartRef={chartRef}
        chartData={chartData}
      />
    </div>
  );
};

export default TakehomePayCalculatorPage;
